import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomAppeal, BoardroomAppealPage, BoardroomAppealRow } from '../../../types';
import AppealCloud, { words } from './AppealCloud';
import DataTable from './DataTable';
import type { Column } from './tableColumns';

/**
 * Appeal: how each company pitches itself, and how each of its pages sells. Who it is for,
 * the problem it meets, its main promise, the marketing angles its pages lead with, the
 * persuasion they show, its tone and its call to action, plus what each of its email forms
 * makes a visitor want. Angles and persuasion are the shared values in backend
 * boardroom/taxonomy.py, the same in every room. A model reads a few of the pages we
 * already have for each company; nothing here starts a fetch. A company is read once and
 * again only when its pages change. See backend boardroom/appeal.py.
 */

type Row = BoardroomAppealRow;

/** Where a company's read stands, in a few words. */
export function stateText(r: Row): string {
  switch (r.state) {
    case 'read':
      return r.due ? 'Read; pages changed since' : 'Read';
    case 'working':
      return 'Reading...';
    case 'due':
      return 'Not read yet';
    case 'cannot_tell':
      return 'Pages say too little';
    case 'failed':
      return `Failed${r.note ? `: ${r.note}` : ''}`;
    case 'no_credits':
      return 'Not enough credits';
    case 'no_site':
      return 'No website';
    default:
      return 'No pages read yet';
  }
}

export { words };

/** One page in a line, e.g. "pricing (buying): save money, all in one". */
export function pageText(p: BoardroomAppealPage): string {
  const intent = p.intent ? ` (${p.intent})` : '';
  const angles = (p.angles ?? []).map(words).join(', ');
  return `${shortPath(p.url)}${intent}${angles ? `: ${angles}` : ''}`;
}

/** Page intents, e.g. "buying: pricing · learning: blog/post". */
export function intentText(r: Row): string {
  return r.pages
    .filter(p => p.intent)
    .map(p => `${p.intent}: ${shortPath(p.url)}`)
    .join(' · ');
}

function shortPath(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/|\/$/g, '');
  return path || 'home';
}

export default function AppealReport({
  data: initial,
  onOpen,
  roomName,
  boardroomId,
}: {
  data: BoardroomAppeal;
  onOpen: (itemId: number) => void;
  roomName?: string;
  /** The room, so "Read appeal" knows where it is. Without it the button is hidden. */
  boardroomId?: number;
}) {
  const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline text-left';
  const MUTED = 'text-gray-500 dark:text-gray-400';
  const CHIP =
    'inline-block mr-1 mb-0.5 px-1.5 rounded bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300';
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  // THE CLOUD FIRST: ten columns of sentences per company were hard to read, and the
  // question is usually "who uses this angle", which a click on a tag answers.
  const [view, setView] = useState<'cloud' | 'table'>('cloud');
  const [message, setMessage] = useState<{ text: string; bad?: boolean } | null>(null);
  useEffect(() => setData(initial), [initial]);

  const refresh = useCallback(async () => {
    if (boardroomId == null) return;
    try {
      setData(await api.getBoardroomReport<BoardroomAppeal>(boardroomId, 'appeal'));
    } catch {
      // The next poll tries again.
    }
  }, [boardroomId]);

  const running = data.rows.some(r => r.state === 'working');
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [running, refresh]);

  const readAll = async () => {
    if (boardroomId == null) return;
    setBusy(true);
    setMessage(null);
    try {
      const got = await api.startBoardroomAppealReads(boardroomId);
      setMessage({ text: `Started ${got.started} read${got.started === 1 ? '' : 's'}.` });
      await refresh();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Could not start the reads', bad: true });
    } finally {
      setBusy(false);
    }
  };

  const chips = (values: string[]) => values.map(v => (
    <span key={v} className={CHIP}>
      {words(v)}
    </span>
  ));

  const columns: Column<Row>[] = [
    {
      key: 'title',
      label: 'Company',
      width: 12,
      always: true,
      value: r => r.title,
      cell: r => (
        <div className="space-y-0.5">
          <button type="button" onClick={() => onOpen(r.id)} className={LINK}>
            {r.title}
          </button>
          <div className={MUTED}>{stateText(r)}</div>
        </div>
      ),
    },
    {
      key: 'audience',
      label: 'Who it is for',
      width: 13,
      value: r => [...r.audiences, ...r.roles, r.audience ?? ''].join(' '),
      cell: r => (
        <div className="space-y-0.5">
          {r.audiences.length > 0 && <div>{chips(r.audiences)}</div>}
          {r.roles.length > 0 && <div className={MUTED}>Roles: {r.roles.join(', ')}</div>}
          {r.audience && <div>{r.audience}</div>}
        </div>
      ),
    },
    { key: 'problem', label: 'Problem it meets', width: 12, value: r => r.problem ?? '' },
    { key: 'promise', label: 'Main promise', width: 14, value: r => r.promise ?? '' },
    {
      key: 'angles',
      label: 'Leads with',
      width: 10,
      value: r => r.angles.map(words).join(', '),
      cell: r => <div>{chips(r.angles)}</div>,
    },
    { key: 'persuasion', label: 'Persuasion', width: 9, value: r => r.persuasion.map(words).join(', ') },
    { key: 'tone', label: 'Tone', width: 7, value: r => r.tone ?? '' },
    { key: 'cta', label: 'Asks visitors to', width: 9, value: r => r.call_to_action ?? '' },
    {
      key: 'pages',
      label: 'Each page',
      width: 16,
      value: r => r.pages.map(p => `${pageText(p)} ${p.message ?? ''}`).join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.pages.map(p => (
            <li key={p.url}>
              <a href={p.url} target="_blank" rel="noopener noreferrer" className={LINK}>
                {shortPath(p.url)}
              </a>
              {p.intent && <span className={MUTED}> ({p.intent})</span>}
              {(p.angles ?? []).length > 0 && <>: {(p.angles ?? []).map(words).join(', ')}</>}
              {p.message && <div className={MUTED}>&ldquo;{p.message}&rdquo;</div>}
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'forms',
      label: 'Email form appeal',
      width: 14,
      value: r => r.forms.map(f => f.appeal).join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.forms.map(f => (
            <li key={f.url}>
              <a href={f.url} target="_blank" rel="noopener noreferrer" className={LINK}>
                {shortPath(f.url)}
              </a>
              : {f.appeal}
              {f.angle && <span className={MUTED}> ({words(f.angle)})</span>}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  const due = data.due;
  const families = data.families ?? [];
  const angles = data.angles ?? [];
  const tactics = data.tactics ?? [];
  const audiences = data.audiences ?? [];
  const ideas = data.ideas ?? [];
  const openFamilies = data.open_families ?? [];
  const counted = (label: string, rows: { name: string; n: number }[]) =>
    rows.length > 0 && (
      <p className={MUTED}>
        {label}:{' '}
        {rows
          .slice(0, 8)
          .map(x => `${x.name} ${x.n}`)
          .join(' · ')}
      </p>
    );
  return (
    <div className="min-w-0 space-y-2 text-xs" data-testid="appeal">
      <p className={MUTED}>
        Read for {data.read} of {data.companies} companies. A model reads a few of the pages we already have for each
        one: the home page, pages with an email form and pages that make the pitch. It reads a company again only when
        its pages change. {data.no_pages > 0 && `${data.no_pages} have no pages read yet.`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {boardroomId != null && (
          <>
          {due > 0 && (
            <button
              type="button"
              onClick={readAll}
              disabled={busy}
              className="px-2 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
            >
              {busy ? 'Starting...' : `Read appeal (${due})`}
            </button>
          )}
          {running && <span className={MUTED}>Reading {data.working}...</span>}
          {message && (
            <span className={message.bad ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}>
              {message.text}
            </span>
          )}
          </>
        )}
        <span className="ml-auto flex gap-1">
          {(['cloud', 'table'] as const).map(k => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              className={`rounded px-1.5 py-0.5 ${
                view === k
                  ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              data-testid={`appeal-view-${k}`}
            >
              {k === 'cloud' ? 'Cloud' : 'Table'}
            </button>
          ))}
        </span>
      </div>
      {view === 'table' && counted(
        'Leads with',
        families.map(f => ({ name: f.label, n: f.companies })),
      )}
      {view === 'table' && counted(
        'Angles',
        angles.map(a => ({ name: words(a.angle), n: a.companies })),
      )}
      {view === 'table' && counted(
        'Persuasion',
        tactics.map(t => ({ name: words(t.tactic), n: t.companies })),
      )}
      {view === 'table' && counted(
        'Who they sell to',
        audiences.map(a => ({ name: a.label, n: a.companies })),
      )}
      {openFamilies.length > 0 && (
        <p className={MUTED}>No rival leads with: {openFamilies.map(f => f.label).join(' · ')}</p>
      )}
      {ideas.length > 0 && (
        <div className="space-y-0.5" data-testid="appeal-ideas">
          <div className="font-medium text-gray-700 dark:text-gray-300">Your ideas</div>
          <ul className="space-y-0.5">
            {ideas.map(i => (
              <li key={i.id}>
                <button type="button" onClick={() => onOpen(i.id)} className={LINK}>
                  {i.title}
                </button>
                {(i.angles ?? []).length > 0 && <span className="ml-1">{chips(i.angles ?? [])}</span>}
                {': '}
                {i.angle ?? <span className={MUTED}>no angle yet</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {view === 'cloud' ? (
        <AppealCloud data={data} onOpen={onOpen} />
      ) : (
        <DataTable
          table="appeal"
          // EVERY CELL HERE IS SENTENCES AND CHIPS. One line per cell cut them at the
          // column edge and ran them into the next column; wrapped, they read.
          defaultMode="wrap"
          room={roomName}
          file="appeal"
          columns={columns}
          rows={data.rows}
          rowKey={r => `appeal-row-${r.id}`}
          empty="No companies in this room yet."
        />
      )}
    </div>
  );
}
